import { PrinterOutlined } from "@ant-design/icons";
import { Button, Modal, message } from "antd";
import dayjs from "dayjs";
import html2canvas from "html2canvas";
import React, { useRef, useState, useEffect } from "react";
import Barcode from "react-barcode";
import { useAuth } from "../../../../contexts/AuthContext";
import { getEmpresa } from "../../../../services/empresaService";
import { calculateLineAmounts } from "../../../../utils/invoiceTotals";
import { formatNumberByCountry } from "../../../../utils/formatNumbers";
import "../../InvoiceStyles.css";

const InvoiceTicket = ({
	open,
	onClose,
	invoice,
	lines,
	caja,
	turno,
	paymentLines = [],
	selectedClient,
	tpvNo = null,
	selectedTPV = null,
	country
}) => {
	const componentRef = useRef(null);
	const { currentUser } = useAuth();
	const [empresaData, setEmpresaData] = useState(null);
	const [tpvData, setTPVData] = useState(null);

	// Formatear la fecha y hora actual
	const now = dayjs();
	const formattedDate = now.format("DD/MM/YYYY");
	const formattedTime = now.format("HH:mm:ss");

	// Cargar datos de la empresa
	useEffect(() => {
		const loadEmpresaData = async () => {
			try {
				const empresa = await getEmpresa();
				setEmpresaData(empresa);
			} catch (error) {
				console.error("Error al cargar datos de la empresa:", error);
				// Continuar sin datos de empresa
			}
		};

		if (open) {
			loadEmpresaData();
		}
	}, [open]);

	// Cargar datos del TPV
	useEffect(() => {
		const loadTPVData = async () => {
			try {
				// Si ya viene la información del TPV desde InvoiceList, usarla
				if (selectedTPV) {
					setTPVData(selectedTPV);
					return;
				}

				// Si solo tenemos el número del TPV desde InvoiceForm, cargarlo
				if (tpvNo) {
					const { getTPVByNoOrId } = await import("../../../../services/tpvService");
					const tpv = await getTPVByNoOrId(tpvNo);
					setTPVData(tpv);
				}
			} catch (error) {
				console.error("Error al cargar datos del TPV:", error);
				// Continuar sin datos de TPV
			}
		};

		if (open && (tpvNo || selectedTPV)) {
			loadTPVData();
		}
	}, [open, tpvNo, selectedTPV]);

	// Función para obtener el nombre completo del país
	const getCountryName = (countryCode) => {
		if (countryCode === "EC") return "ECUADOR";
		return countryCode; // Para otros países, devolver el código tal como viene
	};

	// Obtener el descuento máximo de las formas de pago
	const getPaymentMethodDiscount = () => {
		if (!paymentLines || paymentLines.length === 0) return 0;

		// Si hay múltiples formas de pago, no aplicar descuento global
		if (paymentLines.length > 1) return 0;

		// Buscar el descuento más alto entre las formas de pago (solo para una forma de pago)
		const maxDiscount = paymentLines.reduce(
			(max, line) => ((line.Dto || 0) > max ? line.Dto || 0 : max),
			0
		);

		return maxDiscount;
	};

	// Calcular subtotal, descuento e impuesto
	const calculateTotals = () => {
		if (!lines || lines.length === 0) {
			return {
				subtotal: 0,
				discount: 0,
				tax: 0,
				net: 0,
				paymentDiscount: 0,
				finalTotal: 0,
			};
		}

		let subtotal = 0;
		let tax = 0;
		let net = 0;
		let totalLinesDiscount = 0;

		lines.forEach((line) => {
			const { base, vat, total, discount } = calculateLineAmounts({
				unitPrice: line.Unit_Price,
				qty: line.Quantity,
				discountPct: line.Line_Discount_,
				ivaPct: line.PorIva,
				priceIncludesVAT: line.Price_Includes_VAT === true,
				country,
			});

			subtotal += base;
			tax += vat;
			net += total;
			totalLinesDiscount += discount;
		});

		// Descuento por forma de pago
		const paymentDiscount = getPaymentMethodDiscount();
		const paymentDiscountAmount =
			paymentDiscount > 0 ? net * (paymentDiscount / 100) : 0;

		let finalTotal;
		if (paymentLines && paymentLines.length > 1) {
			finalTotal = paymentLines.reduce(
				(sum, line) => sum + (line.Amount || 0),
				0
			);
		} else {
			finalTotal = net - paymentDiscountAmount;
		}

		return {
			subtotal: Number(subtotal.toFixed(2)),
			tax: Number(tax.toFixed(2)),
			totalLinesDiscount: Number(totalLinesDiscount.toFixed(2)),
			net: Number(net.toFixed(2)),
			paymentDiscount,
			paymentDiscountAmount: Number(paymentDiscountAmount.toFixed(2)),
			finalTotal: Number(finalTotal.toFixed(2)),
		};
	};

	const {
		subtotal,
		tax,
		totalLinesDiscount,
		net,
		paymentDiscount,
		paymentDiscountAmount,
		finalTotal,
	} = calculateTotals();

	// Función para convertir el ticket a imagen usando html2canvas
	const convertToImage = async () => {
		try {
			if (!componentRef.current) return null;

			// Mostrar mensaje de procesando
			const processingMsg = message.loading(
				"Preparando ticket para impresión...",
				0
			);

			// Convertir a canvas con mejor calidad
			const canvas = await html2canvas(componentRef.current, {
				scale: 2, // Mejor calidad (2x)
				useCORS: true, // Permitir imágenes de otros dominios
				allowTaint: true,
				backgroundColor: "#ffffff",
				logging: false,
			});

			// Cerrar mensaje de procesando
			processingMsg();

			return canvas;
		} catch (error) {
			console.error("Error al convertir a imagen:", error);
			window.messageApi.error("Error al generar la imagen del ticket");
			return null;
		}
	};

	// Función para imprimir directamente la imagen
	const printImage = async () => {
		try {
			const canvas = await convertToImage();
			if (!canvas) return;

			// Obtener imagen como data URL
			const image = canvas.toDataURL("image/png");

			// Crear ventana para impresión
			const printWindow = window.open("", "_blank");

			if (!printWindow) {
				window.messageApi.warning(
					"El navegador bloqueó la ventana emergente. Por favor, permite ventanas emergentes para imprimir."
				);
				return;
			}

			// Escribir HTML con la imagen
			printWindow.document.write(`
                <html>
                <head>
                    <title>Ticket de factura ${invoice?.No || ""}</title>
                    <style>
                        body {
                            margin: 0;
                            padding: 0;
                            display: flex;
                            justify-content: center;
                        }
                        img {
                            max-width: 100%;
                            height: auto;
                        }
                        @media print {
                            body {
                                width: 80mm; /* Ancho estándar para tickets */
                                margin: 0 auto;
                            }
                        }
                    </style>
                </head>
                <body>
                    <img src="${image}" alt="Ticket" />
                    <script>
                        // Imprimir automáticamente
                        window.onload = function() {
                            setTimeout(function() {
                                window.print();
                                setTimeout(function() { window.close(); }, 500);
                            }, 200);
                        };
                    </script>
                </body>
                </html>
            `);

			printWindow.document.close();

			// Notificar que se ha impreso y cerrar el modal
			setTimeout(() => {
				onClose();
			}, 1000);
		} catch (error) {
			console.error("Error al imprimir:", error);
			window.messageApi.error("Error al imprimir el ticket");
		}
	};

	// Función para descargar la imagen del ticket
	const downloadTicketImage = async () => {
		try {
			const canvas = await convertToImage();
			if (!canvas) return;

			// Convertir canvas a blob
			const image = canvas.toDataURL("image/png");

			// Crear enlace para descarga
			const link = document.createElement("a");
			link.download = `ticket-${invoice?.No || formattedDate.replace(/\//g, "-")
				}.png`;
			link.href = image;
			link.click();

			window.messageApi.success("Imagen descargada correctamente");
		} catch (error) {
			console.error("Error al descargar imagen:", error);
			window.messageApi.error("Error al descargar la imagen");
		}
	};

	return (
		<Modal
			title="Previsualización de Ticket"
			open={open}
			onCancel={onClose}
			footer={[
				<Button key="download" onClick={downloadTicketImage}>
					Descargar Imagen
				</Button>,
				<Button key="close" onClick={onClose}>
					Cerrar
				</Button>,
				<Button
					key="print"
					type="primary"
					icon={<PrinterOutlined />}
					onClick={printImage}
				>
					Imprimir
				</Button>,
			]}
			width={400}
			centered
			className="ticket-preview-modal"
		>
			<div className="ticket-container">
				<div className="ticket-content" ref={componentRef}>
					{/* Cabecera del ticket */}
					<div className="ticket-header">
						<div style={{ textAlign: "center", marginBottom: "20px" }}>
							<div
								style={{
									fontWeight: "bold",
									fontSize: "16px",
									marginBottom: "8px",
								}}
							>
								{empresaData?.Name || "Empresa"}
							</div>
							<div style={{ fontSize: "14px", marginBottom: "8px" }}>
								{empresaData?.VAT_Registration_No || "Sin RUC"}
							</div>
							{tpvData?.nombre && (
								<div style={{ fontSize: "14px", marginBottom: "8px" }}>
									{tpvData.nombre}
								</div>
							)}
							{tpvData?.pais && (
								<div style={{ fontSize: "14px", marginBottom: "8px" }}>
									{getCountryName(tpvData.pais)}
								</div>
							)}
							{tpvData?.nifCif && (
								<div style={{ fontSize: "14px", marginBottom: "8px" }}>
									{tpvData.nifCif}
								</div>
							)}
						</div>

						<div style={{ textAlign: "center", margin: "15px 0" }}>
							<div
								style={{
									border: "1px solid #000",
									display: "inline-block",
									padding: "8px 16px",
								}}
							>
								<strong style={{ fontSize: "16px" }}>TICKET</strong>
							</div>
						</div>

						<div style={{ margin: "20px 0" }}>
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									marginBottom: "3px",
									fontSize: "10px",
								}}
							>
								<span style={{ fontWeight: "bold" }}>
									{invoice?.Document_Type === "Credit Memo"
										? "NOTA DE CRÉDITO:"
										: "FACTURA:"}
								</span>
								<span>
									{invoice?.id ? String(invoice.id).padStart(6, "0") : "000001"}
								</span>
							</div>

							{/* Mostrar factura de referencia para notas de crédito */}
							{invoice?.Document_Type === "Credit Memo" &&
								invoice?.Applies_to_Doc_No && (
									<div
										style={{
											display: "flex",
											justifyContent: "space-between",
											marginBottom: "3px",
											backgroundColor: "#f5f5f5",
											padding: "3px 5px",
											borderRadius: "4px",
											border: "1px solid #d9d9d9",
											fontSize: "10px",
										}}
									>
										<span style={{ fontWeight: "bold" }}>
											APLICA A FACTURA:
										</span>
										<span style={{ fontWeight: "bold" }}>
											{invoice.Applies_to_Doc_No}
										</span>
									</div>
								)}

							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									marginBottom: "3px",
									fontSize: "10px",
								}}
							>
								<span style={{ fontWeight: "bold" }}>CAJA:</span>
								<span>{caja || invoice?.caja || "1"}</span>
								<span style={{ fontWeight: "bold", marginLeft: "20px" }}>
									TURNO:
								</span>
								<span>{turno || invoice?.turno || "1"}</span>
							</div>

							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									marginBottom: "3px",
									fontSize: "10px",
								}}
							>
								<span style={{ fontWeight: "bold" }}>USUARIO:</span>
								<span>{currentUser?.username || "Usuario Sistema"}</span>
							</div>

							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									marginBottom: "3px",
									fontSize: "10px",
								}}
							>
								<span style={{ fontWeight: "bold" }}>FECHA:</span>
								<span>{formattedDate}</span>
							</div>

							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									marginBottom: "3px",
									fontSize: "10px",
								}}
							>
								<span style={{ fontWeight: "bold" }}>HORA:</span>
								<span>{formattedTime}</span>
							</div>

							{/* Nota informativa sobre compra de libros sueltos - solo mostrar si está activado */}
							{invoice?.libros_sueltos && (
								<div
									style={{
										marginTop: "12px",
										padding: "8px",
										backgroundColor: "#f0f8ff",
										border: "1px solid #d1ecf1",
										borderRadius: "4px",
										fontSize: "12px",
										textAlign: "center",
										color: "#0c5460",
									}}
								>
									<strong>NOTA:</strong> El cliente realiza la compra de libros
									sueltos y no kit.
								</div>
							)}
						</div>
					</div>
					<div style={{ borderTop: "1px dashed #000", margin: "15px 0" }}></div>
					{/* Tabla de productos */}
					<div style={{ marginBottom: "20px" }}>
						<div
							style={{
								display: "grid",
								gridTemplateColumns: "2fr 1fr 1fr 1fr",
								gap: "8px",
								fontWeight: "bold",
								marginBottom: "8px",
								borderBottom: "1px solid #000",
								paddingBottom: "4px",
							}}
						>
							<span>PRODUCTO</span>
							<span style={{ textAlign: "center" }}>CANT</span>
							<span style={{ textAlign: "center" }}>PRECIO</span>
							<span style={{ textAlign: "right" }}>TOTAL</span>
						</div>

						{lines &&
							lines.map((line, index) => {
								const quantity = line.Quantity || 0;
								const unitPrice = line.Unit_Price || 0;
								const clientDiscount = line.Client_Discount_ || 0;
								const couponDiscount = line.Coupon_Discount_ || 0;
								const { base: subtotalLine, total: lineTotal, discount: lineDiscount } = calculateLineAmounts({
									unitPrice: line.Unit_Price,
									qty: line.Quantity,
									discountPct: line.Line_Discount_,
									ivaPct: line.PorIva,
									priceIncludesVAT: line.Price_Includes_VAT === true,
									country,
								});

								return (
									<div key={index} style={{ marginBottom: "12px" }}>
										<div
											style={{
												display: "grid",
												gridTemplateColumns: "2fr 1fr 1fr 1fr",
												gap: "8px",
												marginBottom: "4px",
												fontSize: "12px"
											}}
										>
											<span
												style={{ wordWrap: "break-word" }}
											>
												{line.Description}
											</span>
											<span style={{ textAlign: "center" }}>
												{quantity.toFixed(2)}
											</span>
											<span style={{ textAlign: "center" }}>
												{formatNumberByCountry(unitPrice, country, false)}
											</span>
											<span style={{ textAlign: "right", fontWeight: "bold" }}>
												{formatNumberByCountry((lineTotal + lineDiscount), country, false)}
											</span>
										</div>

										{/* Mostrar descuentos separados si existen */}
										{clientDiscount > 0 && (
											<div
												style={{
													fontSize: "12px",
													color: "#666",
													marginLeft: "8px",
												}}
											>
												Dto. cliente {clientDiscount}%: -
												{formatNumberByCountry((subtotalLine * (clientDiscount / 100)), country, false)}
											</div>
										)}
										{couponDiscount > 0 && (
											<div
												style={{
													fontSize: "12px",
													color: "#666",
													marginLeft: "8px",
												}}
											>
												Dto. cupón {couponDiscount}%: -
												{formatNumberByCountry((subtotalLine * (couponDiscount / 100)), country, false)}
											</div>
										)}
									</div>
								);
							})}
					</div>
					<div style={{ borderTop: "1px dashed #000", margin: "15px 0" }}></div>
					{/* Totales */}
					<div style={{ marginBottom: "20px" }}>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								marginBottom: "6px",
							}}
						>
							<span style={{ fontWeight: "bold" }}>SUB-TOTAL:</span>
							<span>{formatNumberByCountry(subtotal + totalLinesDiscount, country, false)}</span>
						</div>

						{totalLinesDiscount > 0 && (
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									marginBottom: "6px",
								}}
							>
								<span style={{ fontWeight: "bold" }}>DESCUENTO:</span>
								<span>- {formatNumberByCountry(totalLinesDiscount, country, false)}</span>
							</div>
						)}

						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								marginBottom: "6px",
							}}
						>
							<span style={{ fontWeight: "bold" }}>IVA:</span>
							<span>{formatNumberByCountry(tax, country, false)}</span>
						</div>

						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								marginBottom: "6px",
							}}
						>
							<span style={{ fontWeight: "bold" }}>TOTAL BRUTO:</span>
							<span style={{ fontWeight: "bold" }}>{formatNumberByCountry(net, country, false)} </span>
						</div>

						{paymentDiscount > 0 &&
							paymentLines &&
							paymentLines.length <= 1 && (
								<div
									style={{
										display: "flex",
										justifyContent: "space-between",
										marginBottom: "6px",
									}}
								>
									<span style={{ fontWeight: "bold" }}>
										DTO FORMA PAGO ({paymentDiscount}%):
									</span>
									<span>-{formatNumberByCountry(paymentDiscountAmount, country, false)}</span>
								</div>
							)}

						<div
							style={{
								borderTop: "1px solid #000",
								paddingTop: "8px",
								marginTop: "8px",
							}}
						>
							<div style={{ display: "flex", justifyContent: "space-between" }}>
								<span style={{ fontWeight: "bold", fontSize: "16px" }}>
									TOTAL A PAGAR:
								</span>
								<span style={{ fontWeight: "bold", fontSize: "16px" }}>
									{formatNumberByCountry(finalTotal, country, false)}
								</span>
							</div>
						</div>
					</div>
					{/* Formas de pago usadas */}
					{paymentLines && paymentLines.length > 0 && (
						<>
							<div
								style={{ borderTop: "1px dashed #000", margin: "15px 0" }}
							></div>
							<div style={{ marginBottom: "20px" }}>
								<div style={{ fontWeight: "bold", marginBottom: "8px" }}>
									FORMAS DE PAGO:
								</div>
								{paymentLines.map((line, index) => (
									<div
										key={index}
										style={{
											display: "flex",
											justifyContent: "space-between",
											marginBottom: "4px",
										}}
									>
										<span>
											{line.Payment_Method}
											{line.Dto > 0 &&
												paymentLines.length <= 1 &&
												` (Dto: ${line.Dto}%)`}
										</span>
										<span style={{ fontWeight: "bold" }}>
											{formatNumberByCountry(line.Amount, country, false)}
										</span>
									</div>
								))}
							</div>
						</>
					)}
					{/* Información del cliente */}
					{selectedClient && (
						<>
							<div
								style={{ borderTop: "1px dashed #000", margin: "15px 0" }}
							></div>
							<div style={{ marginBottom: "20px" }}>
								<div style={{ fontWeight: "bold", marginBottom: "8px" }}>
									DATOS DEL CLIENTE:
								</div>
								{selectedClient.name && (
									<div
										style={{
											display: "flex",
											justifyContent: "space-between",
											marginBottom: "4px",
										}}
									>
										<span>Nombre:</span>
										<span style={{ fontWeight: "bold" }}>
											{selectedClient.name}
										</span>
									</div>
								)}
								{selectedClient.Numero_Identificacion_fiscal && (
									<div
										style={{
											display: "flex",
											justifyContent: "space-between",
											marginBottom: "4px",
										}}
									>
										<span>
											{(selectedClient.Tipo_Documento_SrI ||
												invoice?.Tipo_Documento_SrI) === "RUC"
												? "RUC:"
												: (selectedClient.Tipo_Documento_SrI ||
													invoice?.Tipo_Documento_SrI) === "Pasaporte"
													? "Pasaporte:"
													: "Cédula:"}
										</span>
										<span style={{ fontWeight: "bold" }}>
											{selectedClient.Numero_Identificacion_fiscal}
										</span>
									</div>
								)}
								{selectedClient.Direccion && (
									<div
										style={{
											display: "flex",
											justifyContent: "space-between",
											marginBottom: "4px",
										}}
									>
										<span>Dirección:</span>
										<span style={{ fontWeight: "bold" }}>
											{selectedClient.Direccion}
										</span>
									</div>
								)}
								{selectedClient.Poblacion && (
									<div
										style={{
											display: "flex",
											justifyContent: "space-between",
											marginBottom: "4px",
										}}
									>
										<span>Ciudad:</span>
										<span style={{ fontWeight: "bold" }}>
											{selectedClient.Poblacion}
										</span>
									</div>
								)}
								{selectedClient.E_Mail && (
									<div
										style={{
											display: "flex",
											justifyContent: "space-between",
											marginBottom: "4px",
										}}
									>
										<span>Email:</span>
										<span style={{ fontWeight: "bold" }}>
											{selectedClient.E_Mail}
										</span>
									</div>
								)}
								{selectedClient.Telefono && (
									<div
										style={{
											display: "flex",
											justifyContent: "space-between",
											marginBottom: "4px",
										}}
									>
										<span>Teléfono:</span>
										<span style={{ fontWeight: "bold" }}>
											{selectedClient.Telefono}
										</span>
									</div>
								)}
							</div>
						</>
					)}
					<div style={{ borderTop: "1px dashed #000", margin: "20px 0" }}></div>
					<div style={{ textAlign: "center", marginBottom: "20px" }}>
						<div style={{ fontSize: "16px", fontWeight: "bold" }}>
							¡Gracias por su compra!
						</div>
					</div>

					{/* Mensaje adicional de términos y condiciones */}
					<div style={{ textAlign: "center", marginBottom: "15px" }}>
						<div style={{ fontSize: "12px", fontWeight: "bold", marginBottom: "8px" }}>
							* COMPROBANTE SIN VALIDEZ TRIBUTARIA *
						</div>
						<div style={{ fontSize: "12px", fontWeight: "bold", marginBottom: "8px" }}>
							¡GRACIAS POR TU COMPRA!
						</div>
						<div style={{ fontSize: "11px", marginBottom: "4px" }}>
							PARA CAMBIOS O DEVOLUCIONES SE ACEPTAN
						</div>
						<div style={{ fontSize: "11px", marginBottom: "4px" }}>
							LIBROS EN PERFECTO ESTADO
						</div>
						<div style={{ fontSize: "11px", marginBottom: "4px" }}>
							NO SE REALIZA DEVOLUCIONES DE EFECTIVO
						</div>
						<div style={{ fontSize: "11px", fontWeight: "bold", marginTop: "8px" }}>
							IMPRESCINDIBLE PRESENTAR TICKET DE COMPRA
						</div>
					</div>

					<div
						style={{
							textAlign: "center",
							marginBottom: "15px",
							padding: "8px",
							backgroundColor: "#fff2e8",
							border: "1px solid #ffa940",
							borderRadius: "4px",
						}}
					>
						<div
							style={{ fontSize: "12px", fontWeight: "bold", color: "#ad6800" }}
						>
							DOCUMENTO SIN VALIDEZ TRIBUTARIA
						</div>
					</div>
					<div style={{ textAlign: "center", marginTop: "20px" }}>
						<Barcode
							value={
								invoice?.id ? String(invoice.id).padStart(6, "0") : "000001"
							}
							width={1.5}
							height={40}
							fontSize={12}
							marginTop={10}
							displayValue={true}
						/>
					</div>
				</div>
			</div>
		</Modal>
	);
};

export default InvoiceTicket;
